/**
 * Thin fetch wrapper for the admin UI's backend calls.
 *
 * - `credentials: 'include'` so the session cookie (set by /auth/login)
 *   rides on every authenticated request. Same-origin in prod
 *   (/admin/* and /auth/* both served by the Fastify instance) so this
 *   is mostly a no-op, but the Vite dev proxy on :4321 still needs it.
 * - Throws `ApiError` on non-2xx. Callers in the AuthContext catch and
 *   inspect `.status` to discriminate 401 vs other failures.
 * - JSON-only — every OCP endpoint we touch from React produces JSON
 *   (the /admin/setup HTML form is server-rendered, not consumed here).
 * - CSRF (OCP-125): for every mutating method (POST/PATCH/PUT/DELETE)
 *   reads the `__Host-orboto_csrf` cookie set by /auth/login and replays it
 *   in the `X-CSRF-Token` header. The Fastify CSRF plugin compares
 *   the two on the server; mismatch → 403 csrf_invalid.
 * - CSRF drift recovery (OCP-208): in-flight sessions that pre-date
 *   the CSRF deploy hit 403 csrf_missing on the first mutation because
 *   their cookie was never minted. The server's `maybeIssueMissing
 *   CsrfCookie` hook mints the cookie on the NEXT GET — so this
 *   helper transparently pings /auth/me on 403 csrf_invalid /
 *   csrf_missing, then retries the original request once with the
 *   freshly-minted cookie. If the retry also fails, the original
 *   error surfaces normally.
 */
import { CSRF_HEADER_NAME, OPERATOR_CSRF_COOKIE_NAME } from '@orboto/ocp-shared';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API error ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';

  const doFetch = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (isMutatingMethod(method)) {
      const csrf = readCookie(OPERATOR_CSRF_COOKIE_NAME);
      if (csrf) headers[CSRF_HEADER_NAME] = csrf;
    }
    return fetch(path, {
      method,
      credentials: 'include',
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  };

  let res = await doFetch();

  // CSRF drift recovery (OCP-208) — see header comment.
  if (res.status === 403 && isMutatingMethod(method)) {
    const peekText = await res.text();
    const peekBody: unknown = peekText ? safeParseJson(peekText) : undefined;
    const errKey = readErrorKey(peekBody);
    if (errKey === 'csrf_invalid' || errKey === 'csrf_missing') {
      try {
        await fetch('/auth/me', { credentials: 'include' });
      } catch {
        // Ping failed (network blip) — fall through to the retry; if
        // that also fails the original-style error will surface.
      }
      res = await doFetch();
    } else {
      // Not a CSRF error — preserve the original semantics: parsed
      // body in ApiError, status 403.
      throw new ApiError(403, peekBody);
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body: unknown = text ? safeParseJson(text) : undefined;

  if (!res.ok) {
    throw new ApiError(res.status, body);
  }
  return body as T;
}

export function readErrorKey(body: unknown): string | null {
  if (body && typeof body === 'object' && 'error' in body) {
    const e = (body as { error: unknown }).error;
    return typeof e === 'string' ? e : null;
  }
  return null;
}

function isMutatingMethod(method: string): boolean {
  return method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
}

/**
 * Read a single cookie value by name from `document.cookie`. Returns
 * null when not present. SSR-safe — the document check makes this
 * usable from setup files / vitest without throwing.
 */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie;
  if (!raw) return null;
  // document.cookie format: 'a=1; b=2; __Host-orboto_csrf=hex…'
  // Browsers replace same-name cookies on Set-Cookie, but jsdom under
  // tests can leave a deleted-but-not-collected empty placeholder
  // alongside a freshly-set real value. Skip empty values so the real
  // token wins regardless of pair order.
  const pairs = raw.split(/;\s*/);
  const prefix = `${name}=`;
  let lastNonEmpty: string | null = null;
  for (const pair of pairs) {
    if (pair.startsWith(prefix)) {
      const value = decodeURIComponent(pair.slice(prefix.length));
      if (value) lastNonEmpty = value;
    }
  }
  return lastNonEmpty;
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
